import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { CheckCircle2, Download, Info, Loader2, Lock } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  downloadCierrePdf,
  finalizeCierre,
  getCierre,
  updateCierreInputs,
} from "@/services/cierreService";
import { IvaAnualCard } from "@/components/features/accountant/cierre/IvaAnualCard";
import { ActivoFijoCard } from "@/components/features/accountant/cierre/ActivoFijoCard";
import { ResultadosCard } from "@/components/features/accountant/cierre/ResultadosCard";
import { SituacionCard } from "@/components/features/accountant/cierre/SituacionCard";

function money(n: number): string {
  return n.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Years offered in the ejercicio selector: current down to current − 5. */
function buildYearOptions(): number[] {
  const current = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, i) => current - i);
}

/**
 * "Cierre fiscal" tab on the accountant tenant-detail page. Year-end tax
 * closing replicating the accountant's workbook: IVA annual reconciliation
 * (auto), and — in later phases — Activo Fijo, IRAE and Impuesto al Patrimonio.
 * Only applies to non-IVA-mínimo tenants.
 */
export function CierreFiscalTab({ tenantId }: { tenantId: string }): JSX.Element {
  const { t } = useTranslation();
  const years = buildYearOptions();
  const [fiscalYear, setFiscalYear] = useState(years[1] ?? years[0]); // default: last closed year

  const queryClient = useQueryClient();
  const cierreQuery = useQuery({
    queryKey: ["accountant", "cierre", tenantId, fiscalYear],
    queryFn: () => getCierre(tenantId, fiscalYear),
    staleTime: 30_000,
  });

  const finalize = useMutation({
    mutationFn: () => finalizeCierre(tenantId, fiscalYear),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["accountant", "cierre", tenantId, fiscalYear],
      });
      toast.success(t("cierre.finalized", "Cierre finalizado"));
    },
    onError: () => toast.error(t("cierre.finalizeError", "No pudimos finalizar")),
  });

  const exportPdf = useMutation({
    mutationFn: () => downloadCierrePdf(tenantId, fiscalYear),
    onError: () => toast.error(t("cierre.pdfError", "No pudimos generar el PDF")),
  });

  // IRAE mínimo (mensual) — edited inline in the hero.
  const [minimoInput, setMinimoInput] = useState("");
  useEffect(() => {
    const m = cierreQuery.data?.resultados?.iraeMinimoMensualUyu;
    if (m != null) setMinimoInput(String(m));
  }, [cierreQuery.data?.resultados?.iraeMinimoMensualUyu]);

  const saveMinimo = useMutation({
    mutationFn: () => {
      const v = parseFloat(minimoInput.replace(/\./g, "").replace(",", "."));
      return updateCierreInputs(tenantId, fiscalYear, {
        params: {
          iraeMinimoMensualUyu: Number.isFinite(v) && v >= 0 ? v : undefined,
        },
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["accountant", "cierre", tenantId, fiscalYear],
      });
      toast.success(t("cierre.minimoSaved", "IRAE mínimo actualizado"));
    },
    onError: () => toast.error(t("cierre.minimoError", "No pudimos guardar")),
  });

  const data = cierreQuery.data;

  return (
    <div className="space-y-4">
      {/* Ejercicio selector */}
      <div className="flex flex-wrap items-center gap-3">
        <Label htmlFor="cierre-ejercicio" className="text-sm font-medium">
          {t("cierre.ejercicio", "Ejercicio")}
        </Label>
        <Select
          value={String(fiscalYear)}
          onValueChange={(v) => setFiscalYear(Number(v))}
        >
          <SelectTrigger id="cierre-ejercicio" className="h-9 w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {cierreQuery.isLoading ? (
        <Card>
          <CardContent className="flex min-h-[160px] items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t("common.loading", "Cargando…")}
          </CardContent>
        </Card>
      ) : !data ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            {t("cierre.loadError", "No pudimos cargar el cierre.")}
          </CardContent>
        </Card>
      ) : !data.applies ? (
        <Card>
          <CardContent className="flex items-start gap-3 py-6 text-sm">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden />
            <div>
              <div className="font-medium text-foreground">
                {t("cierre.noAplica.title", "Cierre no aplicable")}
              </div>
              <div className="text-muted-foreground">
                {data.reason ??
                  t(
                    "cierre.noAplica.default",
                    "Este cierre no aplica para el régimen del cliente.",
                  )}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Resumen impuestos a pagar — dark hero */}
          <div className="relative overflow-hidden rounded-[18px] bg-[var(--fx-ink-900)] px-5 py-5 text-[var(--fx-paper)] shadow-[0_18px_44px_-28px_rgba(22,21,20,0.30)] sm:px-7 sm:py-6">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  "radial-gradient(55% 80% at 100% 0%, rgba(111,165,133,0.22), transparent 60%), radial-gradient(35% 60% at 0% 100%, rgba(111,165,133,0.10), transparent 60%)",
              }}
            />
            <div className="relative">
              <div className="flex items-start justify-between gap-2">
                <div
                  className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase text-[var(--fx-accent-300)]"
                  style={{
                    fontFamily: "var(--fx-ff-mono)",
                    letterSpacing: "0.14em",
                  }}
                >
                  <span
                    aria-hidden
                    className="inline-block h-px w-3 bg-[var(--fx-accent-300)]"
                  />
                  <span>
                    {t("cierre.summary.eyebrow", "Impuestos a pagar")}
                  </span>
                  <span aria-hidden className="text-[var(--fx-accent-300)]/60">
                    ·
                  </span>
                  <span
                    className="font-medium normal-case text-[var(--fx-paper)]/55"
                    style={{ letterSpacing: "0.02em" }}
                  >
                    {t("cierre.ejercicio", "Ejercicio")} {fiscalYear}
                  </span>
                </div>
                {data.status === "finalized" ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[var(--fx-accent-300)]/20 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wide text-[var(--fx-accent-300)]">
                    <CheckCircle2 className="h-3 w-3" />
                    {t("cierre.status.finalized", "Finalizado")}
                  </span>
                ) : null}
              </div>

              <div className="mt-4 flex flex-col">
                <span
                  className="text-[10px] font-semibold uppercase text-[var(--fx-accent-300)]"
                  style={{
                    fontFamily: "var(--fx-ff-mono)",
                    letterSpacing: "0.16em",
                  }}
                >
                  {t("cierre.summary.total", "Total a pagar")}
                </span>
                <span
                  className="mt-1 text-4xl font-medium leading-none tracking-tight tabular-nums text-[var(--fx-paper)] sm:text-[52px]"
                  style={{ fontFamily: "var(--fx-ff-mono)" }}
                >
                  {data.summary.totalUyu != null
                    ? `$U ${money(data.summary.totalUyu)}`
                    : "—"}
                </span>
                <span
                  className="mt-2 text-[10px] font-semibold uppercase text-[var(--fx-paper)]/45"
                  style={{
                    fontFamily: "var(--fx-ff-mono)",
                    letterSpacing: "0.16em",
                  }}
                >
                  UYU
                </span>
              </div>

              <div className="my-5 h-px bg-[var(--fx-paper)]/12" />

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <HeroStat
                  label={t("cierre.summary.irae", "IRAE")}
                  value={data.summary.iraeSaldoUyu}
                />
                <HeroStat
                  label={t("cierre.summary.ip", "Impuesto al Patrimonio")}
                  value={data.summary.ipSaldoUyu}
                />
                <HeroStat
                  label={t("cierre.summary.iva", "Saldo IVA")}
                  value={data.summary.ivaSaldoUyu}
                />
              </div>

              {data.resultados ? (
                <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-[var(--fx-paper)]/12 pt-3">
                  <span
                    className="text-[10px] font-bold uppercase text-[var(--fx-accent-300)]"
                    style={{
                      fontFamily: "var(--fx-ff-mono)",
                      letterSpacing: "0.14em",
                    }}
                  >
                    {t("cierre.minimo.label", "IRAE mínimo")}
                  </span>
                  <span
                    className="flex items-center gap-1 text-sm text-[var(--fx-paper)]"
                    style={{ fontFamily: "var(--fx-ff-mono)" }}
                  >
                    <span className="text-[var(--fx-paper)]/55">$U</span>
                    <input
                      inputMode="decimal"
                      value={minimoInput}
                      onChange={(e) => setMinimoInput(e.target.value)}
                      onBlur={() => saveMinimo.mutate()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                      }}
                      className="w-20 border-b border-[var(--fx-paper)]/30 bg-transparent text-right tabular-nums text-[var(--fx-paper)] outline-none focus:border-[var(--fx-accent-300)]"
                      style={{ fontFamily: "var(--fx-ff-mono)" }}
                      aria-label={t("cierre.minimo.label", "IRAE mínimo")}
                    />
                    <span className="text-xs text-[var(--fx-paper)]/45">
                      {t("cierre.minimo.perMonth", "/mes")}
                    </span>
                    {saveMinimo.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin text-[var(--fx-paper)]/50" />
                    ) : null}
                  </span>
                  <span
                    className="text-xs text-[var(--fx-paper)]/40"
                    style={{ fontFamily: "var(--fx-ff-mono)" }}
                  >
                    × 11 = $U {money(data.resultados.iraeMinimoUyu)}
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          {/* IRAE first — the headline annual tax of the cierre. */}
          {data.resultados ? (
            <ResultadosCard
              tenantId={tenantId}
              fiscalYear={fiscalYear}
              resultados={data.resultados}
              manualInputs={data.manualInputs}
            />
          ) : null}

          {data.situacion ? (
            <SituacionCard
              tenantId={tenantId}
              fiscalYear={fiscalYear}
              situacion={data.situacion}
              manualInputs={data.manualInputs}
            />
          ) : null}

          {data.ivaAnual ? (
            <IvaAnualCard
              tenantId={tenantId}
              fiscalYear={fiscalYear}
              ivaAnual={data.ivaAnual}
              saldoAFavorEjAnteriorUyu={
                data.manualInputs.iva?.saldoAFavorEjAnteriorUyu ?? 0
              }
            />
          ) : null}

          {data.activoFijo ? (
            <ActivoFijoCard
              tenantId={tenantId}
              fiscalYear={fiscalYear}
              activoFijo={data.activoFijo}
              coefRevaluacion={data.coefRevaluacion}
            />
          ) : null}

          {/* Actions */}
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => exportPdf.mutate()}
              disabled={exportPdf.isPending}
            >
              {exportPdf.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-1.5 h-4 w-4" />
              )}
              {t("cierre.downloadPdf", "Descargar PDF")}
            </Button>
            <Button
              type="button"
              variant={data.status === "finalized" ? "outline" : "default"}
              onClick={() => finalize.mutate()}
              disabled={finalize.isPending || data.status === "finalized"}
            >
              {finalize.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Lock className="mr-1.5 h-4 w-4" />
              )}
              {data.status === "finalized"
                ? t("cierre.alreadyFinalized", "Cierre finalizado")
                : t("cierre.finalize", "Finalizar cierre")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function HeroStat({
  label,
  value,
}: {
  label: string;
  value: number | null;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span
        className="text-[9.5px] font-bold uppercase text-[var(--fx-accent-300)]"
        style={{ fontFamily: "var(--fx-ff-mono)", letterSpacing: "0.14em" }}
      >
        {label}
      </span>
      <span
        className="text-lg font-medium leading-none tabular-nums text-[var(--fx-paper)]/80 sm:text-2xl"
        style={{ fontFamily: "var(--fx-ff-mono)" }}
      >
        {value != null ? `$U ${money(value)}` : "—"}
      </span>
    </div>
  );
}
